import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import * as React from "react";
import { Modal, SearchBox, ToggleFilter, useAlert, useDebounce } from "uxp/components";
import { DataSourceEditor } from "./datasource-editor";
import { IContextProvider } from "../../../../uxp";
import { createDataSourceFromAction, DEFAULT_ICON, ICustomJsonDataSource, IDataSource, IModelActionDataSource, ModelDefinition } from "../datasource";
import { generateUUID, hasValue } from "../../../../util";

const JSON_ICON = '/Resources/Lucy/modeldesigner/icons/categories/json.svg'

interface IModelActionBrowserProps {
    context: IContextProvider;
    existingSources: IDataSource[];
    selectedSource: IDataSource;
    onClose: () => void;
    onSelected: (source: IDataSource) => void;
    onRemove: (id: string) => void
    contextData: any;
    renderNotificationBar?: React.ReactNode
}
interface IModelAction {
    Name: string;
    Action: string;
    Icon: string;
    MapKey: string;
}

export const DataSourceBrowser: React.FunctionComponent<IModelActionBrowserProps> = (props) => {

    const alerts = useAlert()

    let [modelSearchText, setModelSearchText] = React.useState('');
    let [existingModelSearchText, setExistingModelSearchText] = React.useState('');
    let [searchResults, setSearchResults] = React.useState<IDataSource[]>([]);
    let [mode, setMode] = React.useState<string>(props.selectedSource ? 'existing' : 'new');
    let [tab, setTab] = React.useState('model-action-recommended')
    let [selectedSource, setSelectedSource] = React.useState(props.selectedSource);

    let sources = props.existingSources || [];

    const debouncedSearchText = useDebounce(modelSearchText, 500);

    React.useEffect(() => {
        if (tab == 'model-action' || tab == 'model-action-recommended') {
            props.context.executeService('UXP', 'WidgetTemplate:SearchModels', { q: debouncedSearchText, max: 20, recommended: tab == 'model-action-recommended' ? '1' : '0' }, { json: true })
                .then((data: IModelAction[]) => {
                    setSearchResults(data.map((x: any) => {
                        return createDataSourceFromAction(x as ModelDefinition);
                    }));
                });
        }
    }, [debouncedSearchText, tab]);

    React.useEffect(() => {
        setSelectedSource(null)
    }, [tab, mode])

    function renderEmpty() {
        return <div className="no-data">No results found</div>;
    }

    function renderEmptyLibrary() {
        return <div className="no-data">No sources have been selected yet. Please select one</div>;
    }

    function selectItem(sr: any) {

        props.onSelected(sr);
    }

    function renderPreviews(items: IDataSource[]) {
        return items.map((sr) => {

            let icon = sr.icon || DEFAULT_ICON
            let name1 = (sr as IModelActionDataSource).model
            let name2 = (sr as IModelActionDataSource).action

            if (sr.type == 'custom-json') {
                icon = JSON_ICON
                name1 = sr.name
                name2 = 'JSON'
            }

            return <div className={classNames('model-preview', sr.type)}
                onClick={() => setSelectedSource(sr)}
            >
                <div className='blk'>
                    <div className="icon" style={{ backgroundImage: `url(${icon})` }} />
                    <div className='model'>{name1}</div>
                    <div className="action">{name2}</div>

                    {hasValue(sr.id) &&
                        <div className="remove"
                            onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()

                                alerts.confirm({
                                    title: "Are you sure?",
                                    content: "You are about to remove a datasource",
                                    confirmButtonTitle: "Yes",
                                    cancelButtonTitle: 'No'
                                })
                                    .then((res: boolean) => {
                                        if (res) props.onRemove(sr.id)
                                    })

                            }}
                        >
                            <FontAwesomeIcon icon={['fas', 'times']} />
                        </div>
                    }

                </div>
            </div>;
        });
    }


    function renderJsonSourceEditor() {

        const newSource: ICustomJsonDataSource = {
            id: '',
            parameters: [],
            description: 'Custom JSON data as a source',
            name: 'CJD-' + generateUUID(),
            type: 'custom-json',
            json: {},
            __source__: null
        }

        return <DataSourceEditor
            onClose={() => { }}
            source={{ ...newSource }}
            contextData={props.contextData}
            onUpdated={(updated) => { selectItem(updated) }}
        />
    }


    function renderContent() {
        if (selectedSource) {
            return <DataSourceEditor
                onClose={() => setSelectedSource(null)}
                source={selectedSource}
                contextData={props.contextData}
                onUpdated={(updated) => { selectItem(updated) }}
            />
        }
        if (mode == 'new') {
            if (tab == 'model-action' || tab == 'model-action-recommended') {
                return <div className="model-results">
                    {searchResults.length == 0 ? renderEmpty() : renderPreviews(searchResults)}
                </div>
            }
            else if (tab == 'custom-json') {
                return <div className="json-editor-container">
                    {renderJsonSourceEditor()}
                </div>
            }
        }
        return <div className="model-results">
            {
                sources.length == 0 ? renderEmptyLibrary() : renderPreviews(sources)
            }
        </div>;
    }


    return <Modal
        title="Select a data source"
        show={true}
        onClose={() => props.onClose()}
        className="wd-model-browser"
        headerContent={(
            <div className={classNames('header', { 'has-notification-bar': !!props.renderNotificationBar })}>
                {!!props.renderNotificationBar
                    ? <div className='data-source-browser-notification-bar-container'>
                        {props.renderNotificationBar}
                    </div>
                    : null}
                <div className='a1'>Select a data source</div>

                <ToggleFilter
                    options={[
                        { label: 'New', value: 'new' },
                        { label: 'Existing', value: 'existing' }
                    ]}
                    value={mode}
                    onChange={setMode}
                />

                {
                    !selectedSource && <div className="a2">
                        <SearchBox
                            value={mode == 'new' ? modelSearchText : existingModelSearchText}
                            onChange={mode == 'new' ? setModelSearchText : setExistingModelSearchText}
                        />
                    </div>
                }
            </div>
        )}

    >
        <div className="source-container">
            {mode == 'new' &&
                <div className="header">
                    <ToggleFilter
                        value={tab}
                        options={[
                            { label: 'Recommended', value: 'model-action-recommended' },
                            { label: 'All Sources', value: 'model-action' },
                            { label: 'Custom JSON', value: 'custom-json' }
                        ]}
                        onChange={setTab}
                        className='new-source-type-selector'
                    />
                </div>
            }

            <div className={classNames("body", { 'has-header': mode == 'new' })}>
                {renderContent()}
            </div>
        </div>
    </Modal>;
};